import { SpecItem } from '@tribal-worldwide-london/fa-configuration-service-vwg';
import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Info } from '@volkswagen-onehub/icons-core';
import { ReactNode, useMemo, useState } from 'react';
import { FeatureStatus, useFeatures } from '@/hooks/useFeatures';
import {
  AutoScrollContainer,
  Box,
  CTAModal,
  FeatureBox,
} from '@/modules/components';
import { SectionNames } from '@/analytics/sectionNames';
import {
  performVWBasicLayerClose,
  performVWBasicLayerLoad,
} from '@/analytics/events';
import useGlobalStore from '@/stores/globalStore';
import { useTribalContext } from '@/context/tribal';
import { StockSpecItem } from '@tribal-worldwide-london/fa-stock-service-vwg';

interface ModelFeaturesItemProps {
  title: ReactNode;
  specItems: SpecItem[] | StockSpecItem[];
  activeSlideIndex: number;
  enableAutoScroll?: boolean;
}

const ModelFeaturesSection = ({
  title,
  specItems,
  activeSlideIndex,
}: ModelFeaturesItemProps) => {
  const { analyticsManager, analyticsBuilder, contentManager } = useTribalContext();
  const { configurations, retailers } = useGlobalStore((state) => state.app);
  const { getSpecItemLabel } = useFeatures();
  const [activeSpecItem, setActiveSpecItem] = useState<
    SpecItem | StockSpecItem
  >();

  const getModalProps = (feature: any) => {
    const modalProps = {
      title: feature.title,
      description:
        feature.title !== feature.description ? feature.description : null,
      shortDescription: feature.shortDescription ? feature.shortDescription : null,
    };
    
    return modalProps;
  };

  const featuresList = useMemo(
    () =>
      specItems.map((specItem: SpecItem | StockSpecItem, i: number) => {
        const label = getSpecItemLabel(specItem.availability, specItem.price);

        if (specItem.title !== specItem.description && !activeSpecItem) {
          setActiveSpecItem(specItem);
        }

        return (
          <Box width="100%" key={`${specItem.availability}${i}`}>
            <FeatureBox status={specItem.availability as FeatureStatus}>
              {label}
            </FeatureBox>
            {specItem.excludedFromFinance && (
              <Box marginTop={DesignTokenSizeEnum.static200}>
                <Text
                  color={TokenTextColor.secondary}
                  appearance={TokenTextAppearance.copy100}
                >
                  {contentManager.get('optional-extras-item-text')}
                </Text>
              </Box>
            )}
          </Box>
        );
      }),
    [specItems]
  );

  const analyticsCommonFields = {
    sectionName: SectionNames.COMPARE_PAGE_MODEL_FEATURES,
    configurations,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  return (
    <Box width="100%">
      {title && (
        <Box
          mb={DesignTokenSizeEnum.static150}
          display="flex"
          gap={DesignTokenSizeEnum.static100}
        >
          <Box
            as={Text}
            tag={TextTag.h4}
            appearance={TokenTextAppearance.bigcopy100}
            bold
            m={DesignTokenSizeEnum.static0}
          >
            {title}
          </Box>
          {activeSpecItem && (
            <CTAModal
              tag="button"
              modalProps={getModalProps(activeSpecItem)}
              openCallback={() =>
                performVWBasicLayerLoad(analyticsCommonFields)
              }
              closeCallback={() =>
                performVWBasicLayerClose(analyticsCommonFields)
              }
            >
              <Info variant="small" />
            </CTAModal>
          )}
        </Box>
      )}
      <AutoScrollContainer activeSlideIndex={activeSlideIndex}>
        {featuresList}
      </AutoScrollContainer>
    </Box>
  );
};

export default ModelFeaturesSection;
